import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Text,
    Alert,
    AlertIcon,
    VStack,
    HStack,
    Heading,
    Card,
    CardBody,
    Avatar,
    Button,
    useColorModeValue,
    Flex,
    Spinner,
    Center,
    IconButton,
    Tooltip,
    useToast,
    Badge,
} from '@chakra-ui/react';
import {
    MdCalendarToday,
    MdArrowBack,
    MdRefresh,
} from 'react-icons/md';
import Icon from 'components/icons/Icon';
import ClientWorkoutCalendar from './ClientWorkoutCalendar';
import { useTrainerClients } from 'services/useTrainerClients';
import { TrainerClient } from 'services/trainerService';
import { useAuth } from 'contexts/AuthContext';

const CalendarPage: React.FC = () => {
    const params = useParams<{ clientId?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { clients, isLoading, error } = useTrainerClients();
    const toast = useToast();
    const { user } = useAuth();

    // Navy theme color modes
    const cardBg = useColorModeValue('white', 'navy.700');
    const borderColor = useColorModeValue('gray.200', 'navy.600');
    const textColor = useColorModeValue('gray.800', 'white');
    const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
    const headerBg = useColorModeValue('white', 'navy.800');

    // Get client information from URL params or find in clients list
    const clientId = params.clientId;
    const clientNameFromUrl = searchParams.get('client_name');

    // Determine user role
    const isAgency = user?.role === 'agency';
    const isTrainer = user?.role === 'trainer';

    // Get selected client info from the clients list
    const selectedClient = useMemo(() => {
        return clients?.find(client => client.id === clientId);
    }, [clients, clientId]);

    // Use the client name from URL if available, otherwise from client data
    const clientName = selectedClient?.name || clientNameFromUrl || 'Unknown Client';

    const handleRefresh = () => {
        window.location.reload();
        toast({
            title: "Refreshing calendar",
            status: "info",
            duration: 2000,
            isClosable: true,
        });
    };

    const handleBackToClients = () => {
        if (isAgency) {
            navigate('/agency/trainers');
        } else {
            navigate('/trainer/clients');
        }
    };

    // Handle redirect logic based on user role
    useEffect(() => {
        if (!clientId) {
            // For agency users, show a calendar overview instead of redirecting
            if (isAgency) {
                // Stay on the calendar page but show agency overview
                return;
            }
            // For trainers, redirect to clients page
            if (isTrainer) {
                navigate('/trainer/clients');
                return;
            }
        }
    }, [clientId, navigate, isAgency, isTrainer]);

    if (isLoading) {
        return (
            <Box pt={{ base: '130px', md: '80px', xl: '80px' }} p={6} minH="100vh">
                <Card bg={cardBg} borderColor={borderColor} borderRadius="20px">
                    <CardBody>
                        <Center h="400px">
                            <VStack spacing={4}>
                                <Spinner size="xl" color="blue.500" thickness="4px" />
                                <Text color={mutedTextColor} fontSize="lg">Loading calendar...</Text>
                                <Text color={mutedTextColor} fontSize="sm">
                                    Preparing workout calendar for {clientName}
                                </Text>
                            </VStack>
                        </Center>
                    </CardBody>
                </Card>
            </Box>
        );
    }

    if (error) {
        return (
            <Box pt={{ base: '130px', md: '80px', xl: '80px' }} p={6} minH="100vh">
                <Alert status="error" borderRadius="20px" bg={cardBg}>
                    <AlertIcon />
                    <Box flex="1">
                        <Text fontWeight="bold">Failed to load calendar</Text>
                        <Text>{error}</Text>
                    </Box>
                    <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </Button>
                </Alert>
            </Box>
        );
    }

    return (
        <Box pt={{ base: '130px', md: '80px', xl: '80px' }} minH="100vh">
            {/* Header */}
            <Box bg={cardBg} borderBottom="1px" borderColor={borderColor} px={6} py={4}>
                <Flex justify="space-between" align="center" mb="20px">
                    <VStack align="start" spacing={1}>
                        <Text color={mutedTextColor} fontSize="14px">
                            {clientId
                                ? "View and manage workout schedule for your client"
                                : isAgency
                                    ? "Agency calendar overview - select a client to view their schedule"
                                    : "Select a client to view their workout schedule"
                            }
                        </Text>
                    </VStack>

                    <HStack spacing={3}>
                        <Tooltip label="Refresh calendar">
                            <IconButton
                                aria-label="Refresh"
                                icon={<Icon as={MdRefresh} />}
                                size="sm"
                                variant="outline"
                                onClick={handleRefresh}
                            />
                        </Tooltip>
                    </HStack>
                </Flex>

                {/* Selected Client Banner - only show if clientId exists */}
                {clientId && (
                    <Card bg={useColorModeValue('blue.50', 'navy.800')} borderColor={useColorModeValue('blue.200', 'navy.600')}>
                        <CardBody py={4}>
                            <HStack spacing={4} justify="space-between">
                                <HStack spacing={4}>
                                    <Avatar
                                        size="md"
                                        name={clientName}
                                        bg="blue.500"
                                    />
                                    <VStack align="start" spacing={1}>
                                        <Text fontWeight="semibold" color={textColor} fontSize="md">
                                            {clientName}
                                        </Text>
                                        {selectedClient ? (
                                            <Text color={mutedTextColor} fontSize="sm">
                                                {selectedClient.email}
                                            </Text>
                                        ) : (
                                            <Text color={mutedTextColor} fontSize="sm">
                                                Client ID: {clientId}
                                            </Text>
                                        )}
                                    </VStack>
                                </HStack>
                                <HStack spacing={3}>
                                    {selectedClient && (
                                        <Badge
                                            colorScheme={selectedClient.is_active ? "green" : "gray"}
                                            size="sm"
                                        >
                                            {selectedClient.is_active ? "Active" : "Inactive"}
                                        </Badge>
                                    )}
                                    <Badge colorScheme="blue" size="sm">Calendar View</Badge>
                                </HStack>
                            </HStack>
                        </CardBody>
                    </Card>
                )}
            </Box>

            {/* Calendar Content */}
            <Box>
                {clientId ? (
                    <ClientWorkoutCalendar
                        clientId={clientId}
                        clientName={clientName}
                        userRole={user?.role}
                    />
                ) : (
                    <Center h="400px">
                        <VStack spacing={4}>
                            {isAgency ? (
                                <>
                                    <Text color={textColor} fontSize="lg" fontWeight="medium">
                                        Agency Calendar Overview
                                    </Text>
                                    <Text color={mutedTextColor} fontSize="sm" textAlign="center">
                                        Welcome to the agency calendar view.<br />
                                        Select a specific client from the trainer management page to view their workout schedule.
                                    </Text>
                                    <Button colorScheme="blue" onClick={handleBackToClients}>
                                        Go to Trainer Management
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Text color={textColor} fontSize="lg">No client selected</Text>
                                    <Button colorScheme="blue" onClick={handleBackToClients}>
                                        Go to Clients
                                    </Button>
                                </>
                            )}
                        </VStack>
                    </Center>
                )}
            </Box>
        </Box>
    );
};

export default CalendarPage;
